<template>
  <div ref="rootEl" class="t360-search" @keydown="onKeydown">
    <div class="t360-input-wrap">
      <span class="t360-search-icon">
        <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
      </span>
      <input
        ref="inputEl"
        v-model="query"
        type="text"
        class="t360-input"
        name="query"
        placeholder="Поиск рейса (Ctrl+K)"
        @input="onInput"
        @focus="onFocus"
      />
      <span v-if="isLoading" class="t360-search-spinner"></span>
    </div>

    <div v-if="isDropdownOpen" class="t360-dropdown">
      <div v-if="!normalizedQuery && !historyRows.length" class="t360-hint">Введите номер рейса, UID или meta-id</div>
      <template v-else-if="!normalizedQuery">
        <div class="t360-history-header">Недавние</div>
        <button
          v-for="(item, index) in historyRows"
          :key="item.key"
          type="button"
          class="t360-option"
          :class="{ selected: index === activeIndex }"
          @mouseenter="activeIndex = index"
          @click="openHistoryItem(item)"
        >
          <span class="opt-id">{{ item.distr_id || item.key }}</span>
          <span class="opt-name">{{ item.display_name || '—' }}</span>
          <span class="status-pill" :class="statusClass(item.status_name)">{{ item.status_name || '—' }}</span>
          <span class="opt-route">{{ item.city_out || '—' }} → {{ item.city_in || '—' }}</span>
        </button>
      </template>
      <div v-else-if="isLoading" class="t360-hint">Поиск…</div>
      <div v-else-if="error" class="t360-hint t360-hint-error">{{ error }}</div>
      <div v-else-if="!rows.length" class="t360-hint">Ничего не найдено</div>
      <template v-else>
        <button
          v-for="(row, index) in rows"
          :key="`${row.distr_id}-${row.uid}-${index}`"
          type="button"
          class="t360-option"
          :class="{ selected: index === activeIndex }"
          @mouseenter="activeIndex = index"
          @click="selectRow(row)"
        >
          <span class="opt-id">{{ row.distr_id || row.erp_trip_id || row.uid }}</span>
          <span class="opt-name">{{ row.display_name || '—' }}</span>
          <span class="status-pill" :class="statusClass(row.status_name)">{{ row.status_name || '—' }}</span>
          <span class="opt-route">{{ row.city_out || '—' }} → {{ row.city_in || '—' }}</span>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useTrip360Store } from '@/stores/trip360'
import { searchTrips360 } from '@/backend/trip360Api'
import { normalizeSearchQuery, searchRowKey } from '@/components/trip360/trip360Logic.mjs'

const STATUS_STYLES = {
  'Открыт': 'pill-open',
  'Закрыт': 'pill-closed',
  'Отменен': 'pill-gray',
  'Удален': 'pill-gray',
  'Устарел': 'pill-gray',
  'Срыв': 'pill-red',
  'Отказ': 'pill-orange',
}

const SEARCH_HISTORY_KEY = 'trip360:search-history'
const SEARCH_HISTORY_LIMIT = 10

function loadSearchHistory() {
  try {
    const raw = JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    return raw.filter((item) => item && typeof item.key === 'string')
  } catch {
    return []
  }
}

function writeSearchHistory(items) {
  try {
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(items))
  } catch {
    /* localStorage недоступен — история не критична */
  }
}

export default {
  name: 'Trip360Search',
  setup() {
    const trip360 = useTrip360Store()

    const rootEl = ref(null)
    const inputEl = ref(null)
    const query = ref('')
    const rows = ref([])
    const isLoading = ref(false)
    const error = ref(null)
    const isFocused = ref(false)
    const activeIndex = ref(0)

    const normalizedQuery = computed(() => normalizeSearchQuery(query.value))
    const isDropdownOpen = computed(() => isFocused.value)
    const historyRows = ref(loadSearchHistory())
    const visibleRows = computed(() => (normalizedQuery.value ? rows.value : historyRows.value))

    function pushSearchHistory(item) {
      const next = [item, ...historyRows.value.filter((h) => h.key !== item.key)]
        .slice(0, SEARCH_HISTORY_LIMIT)
      writeSearchHistory(next)
      historyRows.value = next
    }

    let requestSeq = 0

    async function runSearch() {
      const q = normalizedQuery.value
      activeIndex.value = 0
      if (!q) {
        rows.value = []
        error.value = null
        return
      }
      const seq = ++requestSeq
      isLoading.value = true
      error.value = null
      try {
        const found = await searchTrips360(q, 10)
        if (seq !== requestSeq) return
        rows.value = found
      } catch (e) {
        if (seq !== requestSeq) return
        rows.value = []
        error.value = 'Ошибка поиска, попробуйте ещё раз'
      } finally {
        if (seq === requestSeq) isLoading.value = false
      }
    }

    const debouncedSearch = useDebounceFn(runSearch, 400)

    function onInput() {
      if (!normalizedQuery.value) {
        rows.value = []
        error.value = null
        requestSeq++
        isLoading.value = false
        return
      }
      debouncedSearch()
    }

    function onFocus() {
      isFocused.value = true
      activeIndex.value = 0
    }

    function closeAndOpen(key) {
      query.value = ''
      rows.value = []
      isFocused.value = false
      inputEl.value?.blur()
      trip360.openTrip360(key)
    }

    function selectRow(row) {
      const key = searchRowKey(row)
      if (!key) return
      pushSearchHistory({
        key,
        distr_id: row.distr_id,
        display_name: row.display_name,
        status_name: row.status_name,
        city_out: row.city_out,
        city_in: row.city_in,
      })
      closeAndOpen(key)
    }

    function openHistoryItem(item) {
      pushSearchHistory(item)
      closeAndOpen(item.key)
    }

    function onKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (visibleRows.value.length) activeIndex.value = (activeIndex.value + 1) % visibleRows.value.length
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (visibleRows.value.length) activeIndex.value = (activeIndex.value - 1 + visibleRows.value.length) % visibleRows.value.length
      } else if (event.key === 'Enter') {
        const row = visibleRows.value[activeIndex.value]
        if (row) {
          event.preventDefault()
          if (normalizedQuery.value) selectRow(row)
          else openHistoryItem(row)
        }
      } else if (event.key === 'Escape') {
        isFocused.value = false
        inputEl.value?.blur()
      }
    }

    const onGlobalKeydown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        inputEl.value?.focus()
        inputEl.value?.select()
      }
    }

    const onClickOutside = (event) => {
      if (rootEl.value && !rootEl.value.contains(event.target)) {
        isFocused.value = false
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', onGlobalKeydown)
      window.addEventListener('click', onClickOutside)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onGlobalKeydown)
      window.removeEventListener('click', onClickOutside)
    })

    const statusClass = (status) => STATUS_STYLES[status] || 'pill-gray'

    return {
      rootEl,
      inputEl,
      query,
      rows,
      isLoading,
      error,
      activeIndex,
      normalizedQuery,
      isDropdownOpen,
      historyRows,
      onInput,
      onFocus,
      onKeydown,
      selectRow,
      openHistoryItem,
      statusClass,
    }
  },
}
</script>

<style scoped>
.t360-search {
  position: relative;
  width: 460px;
  max-width: 90vw;
}

.t360-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.t360-search-icon {
  position: absolute;
  left: 9px;
  color: #98a2b3;
  font-size: 12px;
  pointer-events: none;
}

.t360-input {
  width: 100%;
  height: 34px;
  padding: 0 28px 0 28px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 13px;
  color: #344054;
  background: #fff;
  outline: none;
}

.t360-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.12);
}

.t360-search-spinner {
  position: absolute;
  right: 9px;
  width: 12px;
  height: 12px;
  border: 2px solid #d0d5dd;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: t360-spin 0.7s linear infinite;
}

@keyframes t360-spin {
  to { transform: rotate(360deg); }
}

.t360-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.16);
  z-index: 1200;
  overflow: hidden;
  padding: 4px;
}

.t360-hint {
  padding: 12px;
  color: #98a2b3;
  font-size: 13px;
  text-align: center;
}

.t360-hint-error {
  color: #b42318;
}

.t360-history-header {
  padding: 6px 8px 4px;
  color: #98a2b3;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.t360-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: none;
  padding: 7px 8px;
  font: inherit;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  border-radius: 6px;
  text-align: left;
}

.t360-option:hover,
.t360-option.selected {
  background: #f0f7ff;
}

.opt-id {
  font-weight: 700;
  white-space: nowrap;
}

.opt-name {
  color: #667085;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.opt-route {
  color: #98a2b3;
  font-size: 12px;
  white-space: nowrap;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.pill-open { background: #eff6ff; color: #1d4ed8; }
.pill-closed { background: #ecfdf3; color: #027a48; }
.pill-gray { background: #f2f4f7; color: #475467; }
.pill-red { background: #fef3f2; color: #b42318; }
.pill-orange { background: #fffaeb; color: #b54708; }
</style>
